import React, { useEffect, useRef, useCallback } from 'react';

const InteractiveHeroVisual = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, active: false });
  const animFrameRef = useRef(null);
  const nodesRef = useRef([]);
  const pulsesRef = useRef([]);
  const timeRef = useRef(0);
  const chainIconsRef = useRef([]);
  const angleRef = useRef(0);

  const initNodes = useCallback((w, h) => {
    const cx = w / 2;
    const cy = h / 2;
    const nodes = [];

    // Inner ring nodes (close orbit)
    for (let i = 0; i < 8; i++) {
      const angle = (Math.PI * 2 / 8) * i;
      const radius = 120 + Math.random() * 30;
      nodes.push({
        baseAngle: angle,
        radius,
        orbitSpeed: 0.0008 + Math.random() * 0.0004,
        size: 3 + Math.random() * 2,
        brightness: 0.6 + Math.random() * 0.4,
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        ring: 0,
      });
    }

    // Middle ring
    for (let i = 0; i < 12; i++) {
      const angle = (Math.PI * 2 / 12) * i + 0.3;
      const radius = 180 + Math.random() * 40;
      nodes.push({
        baseAngle: angle,
        radius,
        orbitSpeed: 0.0005 + Math.random() * 0.0003,
        size: 2 + Math.random() * 2,
        brightness: 0.4 + Math.random() * 0.4,
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        ring: 1,
      });
    }

    // Outer ring (sparse, large orbit)
    for (let i = 0; i < 16; i++) {
      const angle = (Math.PI * 2 / 16) * i + 0.15;
      const radius = 250 + Math.random() * 60;
      nodes.push({
        baseAngle: angle,
        radius,
        orbitSpeed: 0.0003 + Math.random() * 0.0002,
        size: 1.5 + Math.random() * 1.5,
        brightness: 0.3 + Math.random() * 0.3,
        x: cx + Math.cos(angle) * radius,
        y: cy + Math.sin(angle) * radius,
        ring: 2,
      });
    }

    nodesRef.current = nodes;

    // Chain labels orbiting
    const chains = ['ETH', 'SOL', 'BNB', 'SUI'];
    chainIconsRef.current = chains.map((name, i) => ({
      name,
      angle: (Math.PI * 2 / 4) * i,
      radius: 160,
      orbitSpeed: 0.0006,
      size: 11,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const parent = canvas.parentElement;
      const w = parent.offsetWidth;
      const h = parent.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initNodes(w, h);
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        active: true,
      };
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // Draw the Telegram plane icon
    const drawPlane = (ctx, cx, cy, size, glow, rotation) => {
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rotation);

      // Glow effect
      const gradient = ctx.createRadialGradient(0, 0, size * 0.3, 0, 0, size * 2.5);
      gradient.addColorStop(0, `rgba(0, 218, 240, ${0.25 * glow})`);
      gradient.addColorStop(0.5, `rgba(0, 218, 240, ${0.08 * glow})`);
      gradient.addColorStop(1, 'rgba(0, 218, 240, 0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(0, 0, size * 2.5, 0, Math.PI * 2);
      ctx.fill();

      // Hexagonal shield behind plane
      ctx.strokeStyle = `rgba(0, 218, 240, ${0.15 * glow})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI * 2 / 6) * i - Math.PI / 6;
        const r = size * 1.6;
        if (i === 0) ctx.moveTo(Math.cos(a) * r, Math.sin(a) * r);
        else ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
      }
      ctx.closePath();
      ctx.stroke();

      // Paper airplane shape
      const s = size * 0.9;
      ctx.fillStyle = `rgba(0, 218, 240, ${0.9 * glow})`;
      ctx.shadowColor = 'rgba(0, 218, 240, 0.8)';
      ctx.shadowBlur = 20;

      // Main body
      ctx.beginPath();
      ctx.moveTo(s * 1.1, 0);         // nose
      ctx.lineTo(-s * 0.6, -s * 0.7); // top left
      ctx.lineTo(-s * 0.2, 0);        // middle notch
      ctx.lineTo(-s * 0.6, s * 0.7);  // bottom left
      ctx.closePath();
      ctx.fill();

      // Fold line
      ctx.strokeStyle = `rgba(0, 40, 50, 0.5)`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(s * 1.1, 0);
      ctx.lineTo(-s * 0.2, 0);
      ctx.stroke();

      // Accent wing highlight
      ctx.fillStyle = `rgba(0, 255, 255, ${0.3 * glow})`;
      ctx.beginPath();
      ctx.moveTo(s * 1.1, 0);
      ctx.lineTo(-s * 0.6, -s * 0.7);
      ctx.lineTo(-s * 0.2, 0);
      ctx.closePath();
      ctx.fill();

      ctx.shadowBlur = 0;
      ctx.restore();
    };

    // Draw connections between nearby nodes
    const drawConnections = (ctx, nodes, cx, cy, mouse, t) => {
      const maxDist = 130;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            const alpha = (1 - dist / maxDist) * 0.25;
            ctx.strokeStyle = `rgba(0, 218, 240, ${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }

        // Connection to center
        const dcx = nodes[i].x - cx;
        const dcy = nodes[i].y - cy;
        const distC = Math.sqrt(dcx * dcx + dcy * dcy);
        if (distC < 200 && nodes[i].ring === 0) {
          const alpha = (1 - distC / 200) * 0.12;
          ctx.strokeStyle = `rgba(0, 218, 240, ${alpha})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.moveTo(nodes[i].x, nodes[i].y);
          ctx.lineTo(cx, cy);
          ctx.stroke();
        }
      }

      // Mouse attraction lines
      if (mouse.active) {
        for (let i = 0; i < nodes.length; i++) {
          const dx = nodes[i].x - mouse.x;
          const dy = nodes[i].y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            const alpha = (1 - dist / 150) * 0.3;
            ctx.strokeStyle = `rgba(0, 218, 240, ${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.stroke();
          }
        }
      }
    };

    // Energy pulses
    const spawnPulse = (cx, cy, nodes) => {
      if (nodes.length === 0) return;
      const target = nodes[Math.floor(Math.random() * Math.min(8, nodes.length))];
      pulsesRef.current.push({
        x: cx, y: cy,
        tx: target.x, ty: target.y,
        progress: 0,
        speed: 0.015 + Math.random() * 0.01,
      });
    };

    const animate = (timestamp) => {
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      const cx = w / 2;
      const cy = h / 2;
      const mouse = mouseRef.current;
      const nodes = nodesRef.current;
      timeRef.current = timestamp;

      ctx.clearRect(0, 0, w, h);

      // Update node positions (orbiting)
      nodes.forEach(node => {
        node.baseAngle += node.orbitSpeed;
        let nx = cx + Math.cos(node.baseAngle) * node.radius;
        let ny = cy + Math.sin(node.baseAngle) * node.radius;

        // Mouse repulsion/attraction
        if (mouse.active) {
          const dx = nx - mouse.x;
          const dy = ny - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            const force = (120 - dist) / 120 * 15;
            nx += (dx / dist) * force;
            ny += (dy / dist) * force;
          }
        }

        node.x = nx;
        node.y = ny;
      });

      // Draw connections
      drawConnections(ctx, nodes, cx, cy, mouse, timestamp);

      // Draw nodes
      nodes.forEach(node => {
        const pulse = 0.7 + 0.3 * Math.sin(timestamp * 0.003 + node.baseAngle * 3);
        const alpha = node.brightness * pulse;
        const s = node.size * (0.8 + 0.2 * pulse);

        // Glow
        const g = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, s * 4);
        g.addColorStop(0, `rgba(0, 218, 240, ${alpha * 0.5})`);
        g.addColorStop(1, 'rgba(0, 218, 240, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(node.x, node.y, s * 4, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = `rgba(0, 218, 240, ${alpha})`;
        ctx.beginPath();
        ctx.arc(node.x, node.y, s, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw energy pulses
      if (Math.random() < 0.03) spawnPulse(cx, cy, nodes);
      pulsesRef.current = pulsesRef.current.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;

        const px = p.x + (p.tx - p.x) * p.progress;
        const py = p.y + (p.ty - p.y) * p.progress;
        const alpha = Math.sin(p.progress * Math.PI) * 0.9;

        ctx.fillStyle = `rgba(0, 255, 255, ${alpha})`;
        ctx.shadowColor = 'rgba(0, 218, 240, 0.8)';
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(px, py, 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        return true;
      });

      // Draw chain labels
      chainIconsRef.current.forEach(chain => {
        chain.angle += chain.orbitSpeed;
        const x = cx + Math.cos(chain.angle) * chain.radius;
        const y = cy + Math.sin(chain.angle) * chain.radius;

        // Background pill
        const tw = ctx.measureText(chain.name).width;
        ctx.fillStyle = 'rgba(0, 218, 240, 0.08)';
        ctx.strokeStyle = 'rgba(0, 218, 240, 0.25)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        const pw = 36;
        const ph = 20;
        ctx.roundRect(x - pw / 2, y - ph / 2, pw, ph, 10);
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = 'rgba(0, 218, 240, 0.8)';
        ctx.font = `bold ${chain.size}px "SF Mono", "Fira Code", monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(chain.name, x, y);
      });

      // Draw central plane (with subtle float)
      const floatY = Math.sin(timestamp * 0.001) * 6;
      const floatX = Math.cos(timestamp * 0.0007) * 3;
      const glowPulse = 0.85 + 0.15 * Math.sin(timestamp * 0.002);

      // Plane position
      const planeX = cx + floatX;
      const planeY = cy + floatY;

      // Calculate rotation toward mouse
      let targetAngle = 0;
      if (mouse.active) {
        targetAngle = Math.atan2(mouse.y - planeY, mouse.x - planeX);
      }
      // Smooth interpolation
      const diff = targetAngle - angleRef.current;
      const wrapped = Math.atan2(Math.sin(diff), Math.cos(diff));
      angleRef.current += wrapped * 0.08;
      const planeAngle = mouse.active ? angleRef.current : angleRef.current * 0.95;

      drawPlane(ctx, planeX, planeY, 50, glowPulse, planeAngle);

      // Orbiting ring
      ctx.strokeStyle = `rgba(0, 218, 240, ${0.06 + 0.03 * Math.sin(timestamp * 0.001)})`;
      ctx.lineWidth = 0.5;
      ctx.setLineDash([4, 8]);
      ctx.beginPath();
      ctx.arc(cx, cy, 120, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, 200, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [initNodes]);

  return (
    <div className="relative w-full h-full" style={{ minHeight: '500px' }}>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ cursor: 'crosshair' }}
      />
    </div>
  );
};

export default InteractiveHeroVisual;
